import {
  WAKELOOP_UPGRADE_FAILURE_HINTS,
  WAKELOOP_UPGRADE_METADATA_RECOVERY_HINTS,
} from "./runtime/manual-upgrade";

export function resolveCommonHintByMessage(message: string): string[] | null {
  if (message.includes("WakeLoop version metadata is missing")) {
    return resolveUpgradeHintsFromMessage({
      message,
      defaultHints: WAKELOOP_UPGRADE_METADATA_RECOVERY_HINTS,
    });
  }

  if (message.includes("Version check failed (")) {
    return resolveUpgradeHintsFromMessage({
      message,
      defaultHints: WAKELOOP_UPGRADE_FAILURE_HINTS,
    });
  }

  if (message.includes("upgrade failed (exit")) {
    return resolveUpgradeHintsFromMessage({
      message,
      defaultHints: WAKELOOP_UPGRADE_FAILURE_HINTS,
    });
  }

  if (
    message.includes("wakeloop reset requires --yes in non-interactive mode")
  ) {
    return ["wakeloop reset --yes"];
  }

  if (
    message.includes("wakeloop upgrade requires --yes in non-interactive mode")
  ) {
    return ["wakeloop upgrade --yes", "wakeloop upgrade --check"];
  }

  if (message.includes("interactive menu is disabled in agent view")) {
    return [
      "wakeloop --view human space",
      "wakeloop space create --name spaceship",
      "wakeloop space join <space-id>",
      "wakeloop space watch <space-id>",
    ];
  }

  if (
    message.includes("wakeloop start in agent view requires explicit agent")
  ) {
    return [
      "wakeloop --view human start",
      'wakeloop start --view human --kind human --profile-name "Felix"',
      "wakeloop profiles list --view agent",
      'wakeloop start --kind agent --profile-id "<existing-agent-profile-id>"',
      'wakeloop start --kind agent --profile-name "Agent"',
    ];
  }

  return null;
}

const MANUAL_FALLBACK_COMMAND_REGEX = /Manual fallback:\s*`([^`]+)`/;

export function resolveUpgradeHintsFromMessage(input: {
  message: string;
  defaultHints: readonly string[];
}): string[] {
  const manualFallbackCommand = extractManualFallbackCommand(input.message);
  if (!manualFallbackCommand) {
    return [...input.defaultHints];
  }

  const [, ...remainingHints] = input.defaultHints;
  return [manualFallbackCommand, ...remainingHints];
}

export function extractManualFallbackCommand(message: string): string | null {
  const match = message.match(MANUAL_FALLBACK_COMMAND_REGEX);
  return match?.[1] ?? null;
}

export function resolveSpaceHintByMessage(message: string): string[] | null {
  if (
    message.includes("space.password_required") ||
    message.includes("space.password_invalid") ||
    message.toLowerCase().includes("space password required") ||
    message.toLowerCase().includes("space password invalid")
  ) {
    return [
      "wakeloop whoami",
      "wakeloop space list",
      "wakeloop space join <space-id> --password <value>",
      "Ask a Space member to add this profile if this account should already have access.",
    ];
  }

  if (
    message.includes("space.password_too_short") ||
    message.toLowerCase().includes("password too short") ||
    message.toLowerCase().includes("at least 6 characters")
  ) {
    return [
      "Use at least 6 characters for --password",
      "wakeloop space list",
      "wakeloop space password <space-id> --password <new-password>",
      "wakeloop space create --name <name> --password <value>",
    ];
  }

  if (
    includesMissingRequiredArgumentMessage(message, {
      argument: "space",
      commandPrefix: "wakeloop space password",
    })
  ) {
    return [
      "wakeloop space list",
      "wakeloop space password <space-id> --password <value>",
      "wakeloop space password <space-id> --clear",
      "wakeloop space join <space-id> --password <value>",
    ];
  }

  if (
    message.includes(
      "missing required 'name' argument for wakeloop space create"
    )
  ) {
    return [
      "wakeloop --view human space",
      'wakeloop space create --name "spaceship"',
      'wakeloop space create --name "spaceship" --guide "Keep it concise and actionable."',
    ];
  }

  if (
    includesMissingRequiredArgumentMessage(message, {
      argument: "text",
      commandPrefix: "wakeloop space send",
    })
  ) {
    return [
      'wakeloop space send <space-id> "hello world"',
      "wakeloop space history <space-id> --kind text --limit 20",
      "wakeloop space join <space-id>",
    ];
  }

  if (
    message.includes(
      "agent role is required for this space; pass --role <text>"
    ) ||
    message.includes(
      "agent roleInstructions is required for this space; pass --role-instructions <text>"
    )
  ) {
    return [
      "wakeloop space join <space-id> --role <text> --role-instructions <text>",
      "wakeloop space history <space-id> --kind text --limit 20",
      "wakeloop space watch <space-id>",
    ];
  }

  if (
    includesMissingRequiredArgumentMessage(message, {
      argument: "space",
      commandPrefix: "wakeloop space",
    })
  ) {
    return [
      "wakeloop --view human space",
      "wakeloop space history <space-id> --kind text",
      "wakeloop space join <space-id>",
    ];
  }

  return null;
}

export function resolveProfileHintByMessage(message: string): string[] | null {
  if (
    message.includes(
      "wakeloop profile menu is interactive in human view only"
    ) ||
    message.includes("wakeloop profiles menu is interactive in human view only")
  ) {
    return [
      "wakeloop view human",
      "wakeloop profiles",
      "wakeloop profiles list",
      "wakeloop profiles create <profileName> --kind <human|agent>",
      "wakeloop profiles set <profile-id>",
    ];
  }

  if (
    includesMissingRequiredArgumentMessage(message, {
      argument: "profile",
      commandPrefix: "wakeloop profile set",
    })
  ) {
    return [
      "wakeloop --view human profiles set",
      "wakeloop profiles set <profile-id>",
      "wakeloop profiles list",
    ];
  }

  if (
    includesMissingRequiredArgumentMessage(message, {
      argument: "profile",
      commandPrefix: "wakeloop profile delete",
    })
  ) {
    return [
      "wakeloop --view human profiles delete",
      "wakeloop profiles delete <profile-id> --force",
      "wakeloop profiles list",
    ];
  }

  if (
    includesMissingRequiredArgumentMessage(message, {
      argument: "profile",
      commandPrefix: "wakeloop profile update",
    })
  ) {
    return [
      "wakeloop --view human profiles update",
      "wakeloop profiles update <profile-id> --name <new-name>",
      "wakeloop profiles list",
    ];
  }

  if (
    includesMissingRequiredArgumentMessage(message, {
      argument: "profile",
      commandPrefix: "wakeloop profile show",
    })
  ) {
    return [
      "wakeloop --view human profiles show",
      "wakeloop profiles show <profile-id>",
      "wakeloop profiles list",
    ];
  }

  return null;
}

export function includesMissingRequiredArgumentMessage(
  message: string,
  input: { argument: string; commandPrefix: string }
): boolean {
  return (
    message.includes(
      `missing required '${input.argument}' argument for ${input.commandPrefix}`
    ) ||
    message.includes(
      `missing required argument '${input.argument}' for ${input.commandPrefix}`
    )
  );
}
